import React, { useEffect, useState } from "react";
import { api, fmtGNF } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coins, Calculator, RefreshCw, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export default function PayrollFromPointagePage() {
  const now = new Date();
  const yyyymm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(yyyymm);
  const [otThreshold, setOtThreshold] = useState(173);
  const [otMult, setOtMult] = useState(1.5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/sprint-f/payroll/from-pointage", {
        period,
        include_ot_over_hours: parseInt(otThreshold, 10),
        ot_rate_mult: parseFloat(otMult),
      });
      setData(data);
      toast.success(`${data.total_employees} employés — ${data.total_hours}h — brut estimé ${fmtGNF(data.estimated_gross_total)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!data || !data.lines) return;
    const header = ["Employé", "Rôle", "Type", "Heures normales", "Heures supp.", "Sessions", "Absences", "Taux horaire", "Brut estimé"];
    const rows = data.lines.map(l => [
      l.employee_name, l.role, l.employment_type,
      l.hours_worked, l.overtime_hours, l.sessions, l.absence_days,
      l.hourly_rate_gnf, l.estimated_gross,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_from_pointage_${data.period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="payroll-from-pointage-page">
      <div>
        <div className="chip mb-2"><Coins className="h-3 w-3" /><span>PAIE AUTO DEPUIS POINTAGE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Génération auto — Paie horaire</h1>
        <p className="text-slate-500 mt-1">
          Construit un projet de bulletin à partir des heures réellement pointées et des configurations d&apos;employés.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Période (AAAA-MM)</Label>
            <Input value={period} onChange={e => setPeriod(e.target.value)} placeholder="2026-07" data-testid="pf-period" />
          </div>
          <div>
            <Label>Seuil heures supp.</Label>
            <Input type="number" value={otThreshold} onChange={e => setOtThreshold(e.target.value)} data-testid="pf-ot" />
          </div>
          <div>
            <Label>Multiplicateur HS</Label>
            <Input type="number" step="0.1" value={otMult} onChange={e => setOtMult(e.target.value)} data-testid="pf-mult" />
          </div>
          <Button onClick={compute} disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white gap-2" data-testid="pf-compute">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calculator className="h-4 w-4" />Calculer</>}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs uppercase text-slate-500">Employés</div>
              <div className="text-3xl font-extrabold text-[#1E3A8A] tabular-nums">{data.total_employees}</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs uppercase text-slate-500">Heures totales</div>
              <div className="text-3xl font-extrabold text-[#1E3A8A] tabular-nums">{data.total_hours}h</div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-xs uppercase text-slate-500">Brut estimé</div>
              <div className="text-3xl font-extrabold text-[#166534] tabular-nums">{fmtGNF(data.estimated_gross_total)}</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div className="font-heading font-bold text-[#1E3A8A]">Détail par employé</div>
              <Button variant="outline" size="sm" onClick={exportCsv} className="gap-1" data-testid="pf-export"><Download className="h-3 w-3" />CSV</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Employé</th>
                    <th className="px-4 py-2 text-left">Rôle</th>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-right">H. normales</th>
                    <th className="px-4 py-2 text-right">H. supp.</th>
                    <th className="px-4 py-2 text-right">Sessions</th>
                    <th className="px-4 py-2 text-right">Abs.</th>
                    <th className="px-4 py-2 text-right">Taux/h</th>
                    <th className="px-4 py-2 text-right">Brut estimé</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map(l => (
                    <tr key={l.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pf-row-${l.user_id}`}>
                      <td className="px-4 py-2 font-medium">{l.employee_name}</td>
                      <td className="px-4 py-2 text-xs uppercase text-slate-500">{l.role}</td>
                      <td className="px-4 py-2 text-xs">{l.employment_type === "teacher_hourly" ? "Horaire" : "Mensuel"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{l.hours_worked}h</td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#EA580C] font-semibold">{l.overtime_hours}h</td>
                      <td className="px-4 py-2 text-right tabular-nums">{l.sessions}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{l.absence_days || 0}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtGNF(l.hourly_rate_gnf)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold text-[#166534]">{fmtGNF(l.estimated_gross)}</td>
                    </tr>
                  ))}
                  {data.lines.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Aucun pointage pour cette période.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
